/**
 * Get Ready announcement mapper — bridges the backend serializer
 * (`GetReadyAnnouncementSerializer` in `dealership/`) to the camelCase
 * / Date-typed domain shape that the rest of the UI consumes.
 *
 * The backend stamps `dealership` server-side from the `?dealership_id=`
 * query param, so create / update DTOs never include it in the body.
 */
import type {
  GetReadyAnnouncement,
  GetReadyAnnouncementCreateDto,
  GetReadyAnnouncementCreateServerDto,
  GetReadyAnnouncementServerDto,
  GetReadyAnnouncementUpdateDto,
  GetReadyAnnouncementUpdateServerDto,
} from "@/lib/types/getready/announcements";

export const GetReadyAnnouncementMapper = {
  fromResponse(dto: GetReadyAnnouncementServerDto): GetReadyAnnouncement {
    return {
      id: String(dto.id),
      title: dto.title,
      body: dto.body,
      color: dto.color ?? "",
      lifetimeRange: {
        from: new Date(dto.start_at),
        to: new Date(dto.end_at),
      },
      date: new Date(dto.created_at),
      isArchived: dto.is_archived,
      createdByEmail: dto.created_by_email,
      dealershipId: dto.dealership,
    };
  },

  toCreateDto(
    data: GetReadyAnnouncementCreateDto
  ): GetReadyAnnouncementCreateServerDto {
    return {
      title: data.title,
      body: data.body,
      ...(data.color ? { color: data.color } : {}),
      start_at: data.lifetimeRange.from.toISOString(),
      end_at: data.lifetimeRange.to.toISOString(),
    };
  },

  toUpdateDto(
    data: GetReadyAnnouncementUpdateDto
  ): GetReadyAnnouncementUpdateServerDto {
    const out: GetReadyAnnouncementUpdateServerDto = {};
    if (data.title !== undefined) out.title = data.title;
    if (data.body !== undefined) out.body = data.body;
    if (data.color !== undefined) out.color = data.color;
    if (data.lifetimeRange?.from)
      out.start_at = data.lifetimeRange.from.toISOString();
    if (data.lifetimeRange?.to)
      out.end_at = data.lifetimeRange.to.toISOString();
    return out;
  },
};
